'use strict';

// external

// shared imports
import Joi from '~/shared/src/utilities/joiExtended';
import {ItemAttributes} from '~/shared/src/ci/Item';
import {ReferenceAttributes} from '~/shared/src/ci/Reference';
import {DataStructure} from '~/shared/src/utilities/dataStructure';
import {BaseRequestParameters} from '~/shared/src/model/BaseRequest';
import {Message} from '~/shared/src/utilities/message';
import {
    ColumnFilter,
    FilterMatchMode,
    filtersSchema
} from '~/shared/src/validationSchemes/modules/overview';
import {
    Type,
    SvgGraphTransform,
    SvgNodesTransform,
    GraphType,
    Layout
} from '~/shared/src/utilities/graph';
import {Style} from '~/shared/src/utilities/style';
import {Condition} from '~/shared/src/utilities/condition';
import {getValueStringFromMatchModeAndValue} from '~/shared/src/utilities/filterUtils';


/*** method types ****************************************************************************************************/

/*** loadConfig ***/
export type LoadConfigParameters = Partial<BaseRequestParameters> & {
    viewKey : string;
    parameters? : {
        [ parameter : string ] : string;
    };
};
export type LoadConfigResult = {
    success : boolean;
    data : {
        schema : Schema;
        viewName : string;

        configHash : string;

        title : string | Message;
        tabTitle? : string | Message;
        tooltipTitle? : string | Message;
        iconCls : string;

        nodeLabel? : string | Message;
        nodesLabel? : string | Message;
        linkLabel? : string | Message;
        linksLabel? : string | Message;
        groupLabel? : string | Message;
        groupsLabel? : string | Message;

        dataStructures? : DataStructure[];
        groups? : { [ name : string ] : number[] };

        graphType : GraphType;
        layout : Layout;
        type : Type;

        colorItemTypes? : boolean;
        iconNodeOnly? : boolean;
        showLinkCardinality? : boolean;
        showLinkNames? : boolean;
        showTooltips? : boolean;

        style? : Style[];

        svgGraphTransform? : SvgGraphTransform;
        svgNodesTransform? : SvgNodesTransform;
    };
    metaData : {
        metrics : Metric[];

        // debug
        cypher? : string;
        sql? : string;
        parameters? : { [ parameter : string ] : any };
    };
};

export type LoadConfigDataParameters = Partial<BaseRequestParameters> & ( {
    viewId : string;
    viewKey : string;
} );
export type LoadConfigDataResult = {
    success : boolean;
    data : {
        items : ItemAttributes[];
        references : ReferenceAttributes[];
        rootItemId : number;
    };
    metaData : {
        metrics : Metric[];

        // debug
        cypher? : string;
        sql? : string;
        parameters? : {
            [ parameter : string ] : any
        };
    };
};

/*** clearConfig ***/
export type ClearConfigParameters = {
    configHash? : string;
};
export type ClearConfigResult = {
    success : boolean;
    metaData : {
        metrics : Metric[];
    };
};

export type GetViewKeyByNameParameters = {
    viewName : string;
};
export type GetViewKeyByNameResult = {
    success : true;
    data : {
        viewKey? : string;
    };
    metaData : {
        metrics : Metric[];

        // debug
        sql? : string;
        parameters? : { [ parameter : string ] : any };
    };
};

/*** loadData common***/
export type LoadDataCommonParameters = {
    filters? : ColumnFilter[];
    start? : string;
    end? : string;
    parameters? : {
        [ parameter : string ] : string;
    };
};
export const loadDataCommonParameterDefaults = {
    filters: []
};

export const loadDataCommonParametersSchema = Joi.object( {
    filters   : filtersSchema,
    start     : Joi.string(),
    end       : Joi.string(),
    parameters: Joi.object()
        .pattern( /.*/, Joi.alternatives().try( Joi.string().allow( '' ), Joi.number(), Joi.boolean() ) )
        .description( 'The parameters for the query with their values.' )
} );

/*** loadData ***/
export type LoadDataParameters = Partial<BaseRequestParameters> & LoadDataCommonParameters & {
    schema : Schema;
    referenceDirection : 'SrcToDest' | 'DestToSrc' | 'both';
    referenceDepth : number;
} & (
    {
        /**
         * The id of the root item. No further root item information required.
         */
        rootItemId : number;

        rootItemKey? : never;
        rootItemType? : never;
    } |
    {
        /**
         * The ItemType of the root item.
         */
        rootItemType : string;

        /**
         * The key of the root item. If set also rootItemType is required.
         */
        rootItemKey? : string;

        rootItemId? : never;
    }
    );
export type LoadMetaDataRowData = { [ label : string ] : any };
export type LoadMetaDataRowDataMapping = { [ field : string ] : string };
export type LoadDataResult = {
    success : boolean;
    data : {
        items : ItemAttributes[];
        references : ReferenceAttributes[];
    };
    metaData : {
        metrics : Metric[];

        itemsToDataObjects? : { [ dataObjectId : number ] : number[] };
        itemsToLevels? : { [ level : number ] : number[] };

        // debug
        dataStructure? : DataStructure;
        cypher? : string;
        sql? : string;
        parameters? : { [ parameter : string ] : any };
    };
};

/*** loadDataForDataStructureAndCondition ***/
export type LoadDataForDataStructureAndConditionParameters = Partial<BaseRequestParameters> & LoadDataCommonParameters & {
    rootItemId : number;
    dataStructure : DataStructure;
    condition? : Condition;
};

/*** deleteGraph ***/
export type DeleteGraphParameters = {
    viewId : number;
    historizeOnly? : boolean;
};
export type DeleteGraphResult = {
    success : boolean;
    metaData : {
        metrics : Metric[];
    }
};

export type ParsedConditionContent = {
    ids : number[];
    operator? : FilterMatchMode;
    value? : string;
};
export type ParsedCondition = {
    [ alias : string ] : ParsedConditionContent;
};
export type ParsedConditions = {
    [ dataObjectId : number ] : ParsedCondition;
};
export const parseCondition = ( condition : Condition ) => {
    const aliases : string[] = [];

    let retVal : ParsedConditions = {},
        notParsable               = false;

    if ( condition?.operator === 'and' && Array.isArray( condition.leftExpression ) ) {
        for ( const subCondition of condition.leftExpression ) {
            const ids : number[] = [];

            let dataObjectOrigId : number = 0,
                alias : string            = '',
                operator : FilterMatchMode | undefined,
                value : string            = '';

            // subCondition operator is 'and' OR 'or' condition?
            // -> test all subSubCondition operators are not 'and' OR 'or' and have the same alias and aliases are unique
            if ( ( subCondition.operator === 'and' || subCondition.operator === 'or' ) && Array.isArray( subCondition.leftExpression ) ) {
                const subOperator = subCondition.operator === 'and'
                    ? ' && '
                    : ' || ';

                operator = 'custom';

                for ( const subSubCondition of subCondition.leftExpression ) {
                    if ( subSubCondition.operator === 'and' || subSubCondition.operator === 'or' ) {
                        notParsable = true;
                        break;
                    }
                    else if ( subSubCondition.leftExpressionDataDescriptor?.dataObject?.origId && subSubCondition.leftExpressionDataDescriptor?.alias ) {
                        if ( !alias ) {
                            if ( !aliases.includes( subSubCondition.leftExpressionDataDescriptor.alias ) ) {
                                aliases.push( subSubCondition.leftExpressionDataDescriptor.alias );
                            }
                            else {
                                notParsable = true;
                                break;
                            }

                            dataObjectOrigId = subSubCondition.leftExpressionDataDescriptor.dataObject.origId;

                            alias = subSubCondition.leftExpressionDataDescriptor.alias;
                        }
                        else if ( alias !== subSubCondition.leftExpressionDataDescriptor.alias ) {
                            notParsable = true;
                            break;
                        }

                        if ( subSubCondition.operator ) {
                            if ( value ) {
                                value += subOperator;
                            }
                            value += getValueStringFromMatchModeAndValue( subSubCondition.operator, subSubCondition.rightExpression );
                        }

                        if ( subSubCondition.id ) {
                            ids.push( subSubCondition.id );
                        }
                    }
                    else {
                        notParsable = true;
                        break;
                    }
                }
            }
            // test aliases are unique
            else if ( subCondition.leftExpressionDataDescriptor?.dataObject?.origId && subCondition.leftExpressionDataDescriptor?.alias ) {
                if ( !aliases.includes( subCondition.leftExpressionDataDescriptor.alias ) ) {
                    aliases.push( subCondition.leftExpressionDataDescriptor.alias );

                    if ( subCondition.id ) {
                        ids.push( subCondition.id );
                    }
                    if ( Array.isArray( subCondition.rightExpression ) ) {
                        ids.push( ...subCondition.rightExpression.map( rEC => rEC.id ?? 0 ) );
                    }

                    dataObjectOrigId = subCondition.leftExpressionDataDescriptor.dataObject?.origId;

                    alias = subCondition.leftExpressionDataDescriptor.alias;

                    operator = ( subCondition.operator !== 'and' && subCondition.operator !== 'or' )
                        ? subCondition.operator
                        : undefined;

                    value = typeof subCondition.rightExpression === 'string'
                        ? subCondition.rightExpression
                        : Array.isArray( subCondition.rightExpression )
                            ? subCondition.rightExpression.map( rEC => typeof rEC.leftExpression === 'string' ? rEC.leftExpression : '' ).join( '-:,:-' )
                            : '';
                }
                else {
                    notParsable = true;
                }
            }
            else {
                notParsable = true;
            }

            if ( notParsable ) {
                break;
            }

            retVal[ dataObjectOrigId ] = {
                ...retVal[ dataObjectOrigId ],
                [ alias ]: {
                    ids,
                    operator,
                    value
                }
            }
        }

        if ( notParsable ) {
            retVal = {};
        }
    }

    return retVal;
};
